import React from "react";
import { Modal, Box, Typography, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { Invoice } from "../Invoices";
import { Invoice as InvoiceGeneratorType } from "../InvoiceGenerator/data/types";
import { initialInvoice } from "../InvoiceGenerator/data/initialData";
import { EditInvoiceContext } from "../InvoiceGenerator";

interface EditInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  type: "purchase" | "sales" | "expense";
  onSave?: (
    invoice: Invoice,
    newName: string,
    newCategory: string,
    newInvoiceNumber: number,
    newDate: string,
    newSubtotal: number,
    newVatTotal: number,
    newTotal: number,
    newClientName: string,
  ) => void;
}

const EditInvoiceModal: React.FC<EditInvoiceModalProps> = ({
  open,
  onClose,
  invoice,
  type,
  onSave,
}) => {
  const navigate = useNavigate();

  const convertToInvoiceGeneratorFormat = (
    dbInvoice: Invoice,
  ): InvoiceGeneratorType => {
    // Prefer the full invoice snapshot saved when the invoice was created/edited
    let invoiceData: InvoiceGeneratorType = { ...initialInvoice };

    if (dbInvoice.invoiceData) {
      try {
        invoiceData = { ...invoiceData, ...JSON.parse(dbInvoice.invoiceData) };
      } catch (_e) {}
    }

    // Keep these fields in sync with the latest values stored on the invoice record
    return {
      ...invoiceData,
      name: dbInvoice.name || invoiceData.name,
      invoiceTitle: dbInvoice.invoiceNumber.toString(),
      invoiceDate: dbInvoice.date || invoiceData.invoiceDate,
      clientName: dbInvoice.clientName || invoiceData.clientName,
    };
  };

  const handleOpenInvoiceGenerator = () => {
    if (!invoice) return;

    // Prepare the invoice data for the generator
    const invoiceGeneratorData = convertToInvoiceGeneratorFormat(invoice);

    // Store the invoice data in localStorage
    window.localStorage.setItem(
      "invoiceData",
      JSON.stringify(invoiceGeneratorData),
    );

    // Store the edit context
    const editContext: EditInvoiceContext = {
      invoiceId: invoice.id,
      type: type,
      originalInvoice: invoice,
      invoiceNumber: invoice.invoiceNumber,
    };

    window.localStorage.setItem(
      "editInvoiceContext",
      JSON.stringify(editContext),
    );

    // Close the modal and navigate to the generator
    onClose();
    navigate("/generator");
  };

  if (!invoice) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="edit-invoice-modal-title"
      aria-describedby="edit-invoice-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%",
          maxWidth: 500,
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Box display="flex" justifyContent="space-between" mb={2}>
          <Typography id="edit-invoice-modal-title" variant="h6" component="h2">
            Edit Invoice
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Invoice #{invoice.invoiceNumber}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Open the Invoice Generator to edit the full invoice details. You can
          modify all fields including items, amounts, and client information.
          The invoice number will remain the same.
        </Typography>

        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenInvoiceGenerator}
          >
            Open Invoice Generator
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditInvoiceModal;
